import { POD_NAMES, PODS, PULSE_COLORS } from './pulseConfig.js'
import { podNameToKey } from './pulseHelpers.js'

const STATUS_CONFIG = [
    { key: 'passedQa', label: 'COMPLETED', color: '#16A34A', bg: '#064E3B' },
    { key: 'inQa', label: 'IN QA', color: '#3B82F6', bg: '#1E3A8A' },
    { key: 'needsVerification', label: 'NEEDS VERIFICATION', color: '#A78BFA', bg: '#4C1D95' },
    { key: 'inProgress', label: 'IN DEVELOPMENT', color: '#F59E0B', bg: '#78350F' },
]

const PRODUCT_POD_KEYS = new Set(Object.values(PODS).map((p) => p.key))

function filterPods(byPod = [], selectedPod) {
    const productPods = byPod.filter((p) => PRODUCT_POD_KEYS.has(p.podKey))
    if (selectedPod === 'all') return productPods
    const podKey = podNameToKey(selectedPod)
    return podKey ? productPods.filter((p) => p.podKey === podKey) : productPods
}

function filterAiPods(byPod = [], selectedPod) {
    const productPods = byPod.filter((p) => PRODUCT_POD_KEYS.has(p.podKey))
    if (selectedPod === 'all') return productPods
    const podKey = podNameToKey(selectedPod)
    return podKey ? productPods.filter((p) => p.podKey === podKey) : productPods
}

export default function QaStoryStatus({
    passedQa: _passedQa,
    inQa: _inQa,
    needsVerification: _needsVerification,
    inProgress: _inProgress,
    byPod,
    aiUsage,
    selectedPod,
    headerAction,
}) {
    const displayPods = filterPods(byPod, selectedPod)
    const displayAiPods = filterAiPods(aiUsage?.byPod, selectedPod).sort((a, b) => {
        if (b.adoptionPct !== a.adoptionPct) return b.adoptionPct - a.adoptionPct
        return b.withAiCompleted - a.withAiCompleted
    })

    const totals = {
        passedQa: displayPods.reduce((sum, pod) => sum + pod.passedQa, 0),
        inQa: displayPods.reduce((sum, pod) => sum + pod.inQa, 0),
        needsVerification: displayPods.reduce((sum, pod) => sum + pod.needsVerification, 0),
        inProgress: displayPods.reduce((sum, pod) => sum + (pod.inProgress ?? 0), 0),
    }

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    marginBottom: '16px',
                }}
            >
                <div>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px', margin: 0 }}>
                        Work Status
                    </h3>
                    <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: '6px 0 0' }}>
                        Completed, in QA, needs verification, and in-development work across pods.
                    </p>
                </div>
                {headerAction}
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                    gap: '16px',
                    marginBottom: '20px',
                }}
            >
                {STATUS_CONFIG.map((status) => (
                    <div key={status.key} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '30px', fontWeight: 700, color: status.color }}>
                            {totals[status.key]}
                        </div>
                        <span
                            style={{
                                display: 'inline-block',
                                marginTop: '4px',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 700,
                                backgroundColor: status.bg,
                                color: status.color,
                            }}
                        >
                            {status.label}
                        </span>
                    </div>
                ))}
            </div>

            {displayPods.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <div
                        style={{
                            color: PULSE_COLORS.textMuted,
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            marginBottom: '8px',
                        }}
                    >
                        By Pod
                    </div>
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                                <HeaderCell align="left">Pod</HeaderCell>
                                <HeaderCell color="#E879F9">AICompleted</HeaderCell>
                                <HeaderCell color="#FB7185">Not AICompleted</HeaderCell>
                                <HeaderCell color={PULSE_COLORS.success}>AI Usage</HeaderCell>
                                {STATUS_CONFIG.map((status) => (
                                    <HeaderCell key={status.key} color={status.color}>
                                        {status.label}
                                    </HeaderCell>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {displayPods.map((pod) => {
                                const aiPod = displayAiPods.find((entry) => entry.podKey === pod.podKey)
                                return (
                                    <tr key={pod.podKey} style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                                        <Cell align="left" strong>
                                            {POD_NAMES[pod.podKey] ?? pod.podKey}
                                        </Cell>
                                        <Cell color="#E879F9" strong>
                                            {aiPod?.withAiCompleted ?? 0}
                                        </Cell>
                                        <Cell color="#FB7185" strong>
                                            {aiPod?.withoutAiCompleted ?? 0}
                                        </Cell>
                                        <Cell color={PULSE_COLORS.success} strong>
                                            {aiPod?.adoptionPct ?? 0}%
                                        </Cell>
                                        {STATUS_CONFIG.map((status) => (
                                            <Cell
                                                key={`${pod.podKey}-${status.key}`}
                                                color={pod[status.key] > 0 ? status.color : PULSE_COLORS.textSubtle}
                                                strong
                                            >
                                                {pod[status.key] ?? 0}
                                            </Cell>
                                        ))}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

function HeaderCell({ children, color = PULSE_COLORS.textMuted, align = 'center' }) {
    return (
        <th
            style={{
                textAlign: align,
                padding: align === 'left' ? '8px 16px 8px 0' : '8px 12px',
                color,
                fontWeight: 600,
                fontSize: '12px',
            }}
        >
            {children}
        </th>
    )
}

function Cell({ children, color = PULSE_COLORS.text, align = 'center', strong = false }) {
    return (
        <td
            style={{
                textAlign: align,
                padding: align === 'left' ? '8px 16px 8px 0' : '8px 12px',
                color,
                fontWeight: strong ? 600 : 400,
            }}
        >
            {children}
        </td>
    )
}
